import { useModal } from 'components/Modals';
import { createContext, UIEvent, useContext, useState } from 'react';
import { DevicesType } from 'services/resources';
import colors from 'styles/colors';
import FlashMessage from 'components/FlashMessage';
import { FLASH_MESSAGE_TYPES } from 'constants/common';
import { RemoveModal } from 'components/Modals/Remove';
import { useIntl } from 'react-intl';
import { useAppDispatch } from 'hooks/store';
import { disconnectDevice } from 'services/FleetSettings/thunks';

const DeviceDisconnectContext = createContext(undefined);

const DisconnectConfirmModalProvider = ({ children }) => {
  const { formatMessage } = useIntl();
  const { openModal, closeModal, Modal, isOpen } = useModal({
    background: colors.modalBackground,
    programmaticallyOpen: true,
    closeOnOutsideClick: false,
  });

  const dispatch = useAppDispatch();
  const [device, setDevice] = useState<DevicesType | null>(null);

  const [flashMessageVisible, setFlashMessageVisible] = useState(false);
  const [flashMessageType, setFlashMessageType] = useState<FLASH_MESSAGE_TYPES | null>(null);
  const [flashMessage, setFlashMessage] = useState<string | null>(null);

  const addFlashMessage = (text: string, type: FLASH_MESSAGE_TYPES) => {
    setFlashMessageVisible(true);
    setFlashMessageType(type);
    setFlashMessage(text);
  };

  return (
    <DeviceDisconnectContext.Provider value={{ openModal, setDevice, device }}>
      {children}
      {isOpen && (
        <Modal>
          <RemoveModal
            removedItemTitle={formatMessage({ id: 'views.FleetSettings.Telematics.Modals.Disconnect.title' })}
            removedItemDescription={formatMessage({
              id: 'views.FleetSettings.Telematics.Modals.Disconnect.description',
            })}
            confirmButtonLabel={formatMessage({ id: 'views.FleetSettings.Telematics.Modals.Disconnect.confirm' })}
            confirmCallback={async (e: UIEvent) => {
              await dispatch(disconnectDevice(device.id));
              addFlashMessage(
                formatMessage({ id: 'views.FleetSettings.Telematics.Modals.Disconnect.success' }),
                FLASH_MESSAGE_TYPES.success
              );
              closeModal(e);
            }}
            cancelCallback={closeModal}
          />
        </Modal>
      )}
      <FlashMessage
        visible={flashMessageVisible}
        type={flashMessageType}
        text={flashMessage}
        onClose={() => {
          setFlashMessageVisible(false);
        }}
      />
    </DeviceDisconnectContext.Provider>
  );
};

export const useDisconnection = () => {
  const context = useContext(DeviceDisconnectContext);
  if (!context) {
    throw new Error('useForceVin must be used within a ForceVinModalProvider');
  }

  return context;
};

export default DisconnectConfirmModalProvider;
